import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import LoadingIndicator from '../components/LoadingIndicator';
import '../styles/ManageReportsPage.css';
import { getReportsForMenu, changeReportStatus, getParameterDetails } from '../services/manageReportsService';
import axios from '../services/axiosConfig';
import { useSession } from '../context/SessionContext';
import { authService } from '../services/authService';

// Define API URL from environment variable
const API_URL = process.env.REACT_APP_API_URL;
    
const ManageReportsPage = () => {
  const navigate = useNavigate();
  const { userId, userRoles, isAdmin, userName } = useSession();
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [error, setError] = useState(null);
  const [showReportList, setShowReportList] = useState(false);
  const [showKeywordTags, setShowKeywordTags] = useState(true);
  
  // Fetch menus on component mount
  useEffect(() => {
    fetchMenus();
  }, []);
  
  // Fetch menus from API (with Bearer token)
    // Helper to get token from cookie only
    const getAuthTokenFromCookie = () => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; auth_token=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
      return null;
    };

    const fetchMenus = async () => {
      try {
        setLoading(true);
        // Fetch menus from the API (token will be injected by axios interceptor)
        const response = await axios.get(`${API_URL}/GetMenuList`);
        if (response.data.success && response.data.allMenus) {
          // Filter menus with parentMenuId === 0 and exclude "Admin" menu
          const filteredMenus = response.data.allMenus.filter(
            (menu) => menu.parentMenuId === 0 && menu.menuName !== "Admin"
          );
          setMenuItems(filteredMenus);
        } else {
          setMenuItems([]);
          setError('No menus found');
        }
      } catch (err) {
        console.error('Error fetching menus:', err);
        setError('Failed to load menus');
      } finally {
        setLoading(false);
      }
    };
  
  // Function to fetch reports from API using the manageReportsService
  const fetchReports = async (menuId) => {
    if (!menuId) return;
        try {
      setLoading(true);
      setError(null);
            // Use the getReportsForMenu function from manageReportsService.js
      const userRoles = "PowerUser"; // This should come from authentication context in a real app
      const adminUser = true; // This should come from authentication context
        const token = getAuthTokenFromCookie();
        const response = await getReportsForMenu(menuId, userRoles, adminUser, token);
      if (response && response.reports && response.reports.length > 0) {
        // Further processing of reports
      }
            if (response && response.reports) {
        // Transform the API response to match our component's expected format
        const formattedReports = response.reports.map(report => {
          // Determine active status with more comprehensive check
          const isActive = 
            report.Active === true || 
            report.active === true || 
            report.IsActive === true || 
            report.isActive === true ||
            (report.InActive !== undefined && !report.InActive) ||
            (report.inactive !== undefined && !report.inactive) ||
            (typeof report.Active === 'string' && report.Active.toLowerCase() === 'true') ||
            (typeof report.active === 'string' && report.active.toLowerCase() === 'true');
                    return {
            id: report.rptId || report.ReportId || report.reportId,
            name: report.reportName,
            description: report.reportDescription,
            code: report.rptCode || report.ReportCode || report.reportCode,
            path: report.rptPath || report.ReportPath || report.reportPath,
            server: report.rptServer || report.ReportServer || report.reportServer,
            version: report.SSRSVersion || report.ssrsVersion,
            methodologyPath: report.ReportMethodologyPath || report.reportMethodologyPath,
            keywords: report.keywordsAndTags,
            active: isActive,
            menuId: menuId
          };
        });
                setFilteredReports(formattedReports);
        setShowKeywordTags(response.showKeywordTags !== false);
        setShowReportList(true);
      } else {
        setFilteredReports([]);
        setError('No reports found for this menu');
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError('An error occurred while fetching reports');
      setFilteredReports([]); // Clear reports on error
    } finally {
      setLoading(false);
    }
  };
      // Build a hierarchical menu structure
  const buildMenuTree = (items, parentId = null) => {
    return items
      .filter(item => item.parentMenuId === parentId)
      .map(item => ({
        ...item,
        children: buildMenuTree(items, item.id)
      }));
  };
    const menuTree = buildMenuTree(menuItems);
    // Recursive function to render menu options
  const renderMenuOptions = (items, level = 0) => {
    return items.map(item => (
      <React.Fragment key={item.id}>
        <option value={item.id} style={{ paddingLeft: `${level * 20}px` }}>
          {item.name}
        </option>
        {item.children && renderMenuOptions(item.children, level + 1)}
      </React.Fragment>
    ));
  };
    // Handle menu selection change
  const handleMenuChange = (e) => {
    const menuId = parseInt(e.target.value, 10);
    if (!menuId) {
      setSelectedMenu(null);
      setFilteredReports([]);
      setShowReportList(false);
      return;
    }

    const selected = menuItems.find(menu => menu.menuId === menuId);
    setSelectedMenu(selected);
    setSelectedReportId(null);
    setError(null);
        // Fetch reports for the selected menu
    fetchReports(menuId);
  };
    // Handle report selection
  const handleReportSelection = (reportId) => {
    setSelectedReportId(reportId);
    setError(null);
  };
    // Handle Add button click
  const handleAddReport = () => {
    if (!selectedMenu) {
      setError("Please select a menu first");
      return;
    }
    // Use menuId property (not id) to match AddEditReportPage expectations
    const menuId = selectedMenu.menuId || selectedMenu.id;
    navigate(`/add-report?menuId=${menuId}`);
  };
  
  // Handle Edit button click
  const handleEditReport = async () => {
    if (!selectedReportId || !selectedMenu) {
      toast.error('Please select a report and menu to edit');
      return;
    }

    try {
      setLoading(true);

      // Prepare payload for API call
      const payload = {
        rptId: selectedReportId.toString(), // Convert reportId to string
        rptCode: '', // Optional field
        userRoles: userRoles || '',
        adminUser: isAdmin ? 'True' : 'False',
        userId: userId || '',
        menuId: selectedMenu.menuId.toString() // Convert menuId to string
      };

      console.log('Calling GetEditReportDetails API with payload:', payload);
      // const token = await authService.acquireTokenForAPI();
      // Fetch report details from the API using POST request
      const response = await axios.post(
        `${API_URL}/AddEditReport/GetEditReportDetails`,
        payload,
        {
          // headers: {
          //   Authorization: `Bearer ${token}`
          //}
        }
      );

      console.log('API response for GetEditReportDetails:', response.data);

      const reportDetails = response.data;

      // Navigate to the AddEditReportPage with the correct route
      navigate(`/edit-report/${selectedReportId}`, {
        state: { reportDetails },
      });
    } catch (error) {
      console.error('Error fetching report details:', error);
      toast.error('Failed to fetch report details');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle Delete/Deactivate button click
  const handleDeactivateReport = async () => {
    if (!selectedReportId) {
      setError("Please select a report");
      return;
    }
        if (window.confirm('Are you sure you want to deactivate this report?')) {
      try {
        setLoading(true);
                // Get the report to deactivate
        const report = filteredReports.find(report => report.id === selectedReportId);
        if (!report) {
          throw new Error("Report not found");
        }
        
        // Get user info (in a real app, these would come from authentication context)
        const userRoles = "PowerUser"; 
        const adminUser = true;
        const userName = "System"; // In a real app, this would be the logged-in user's name
        
        // Call the API to deactivate the report using the new ChangeReportStatus endpoint
        await changeReportStatus(
          selectedReportId,
          "deactivate",
          selectedMenu.id,
          userRoles,
          adminUser,
          userName
        );
         // Update the local state to reflect the change
        setFilteredReports(
          filteredReports.map(item => 
            item.id === selectedReportId ? { ...item, active: false } : item
          )
        );
                // Show success message
        toast.success("Report Inactivated Successfully");
        setError({ message: "Report Inactivated Successfully", isSuccess: true });
      } catch (err) {
        console.error('Error deactivating report:', err);
        toast.error(`Failed to deactivate report: ${err.message}`);
        setError(`Failed to deactivate report: ${err.message}`);
      } finally {
        setLoading(false);
      }
    }
  };
    // Handle Activate button click
  const handleActivateReport = async () => {
    if (!selectedReportId) {
      setError("Please select a report");
      return;
    }
    if (window.confirm('Are you sure you want to activate this report?')) {
      try {
        setLoading(true);
                // Get the report to activate
        const report = filteredReports.find(report => report.id === selectedReportId);
        if (!report) {
          throw new Error("Report not found");
        }
        
        // Get user info (in a real app, these would come from authentication context)
        const userRoles = "PowerUser"; 
        const adminUser = true;
        const userName = "System"; // In a real app, this would be the logged-in user's name
        
        // Call the API to activate the report using the new ChangeReportStatus endpoint
        await changeReportStatus(
          selectedReportId,
          "activate",
          selectedMenu.id,
          userRoles,
          adminUser,
          userName
        );
               // Update the local state to reflect the change
        setFilteredReports(
          filteredReports.map(item => 
            item.id === selectedReportId ? { ...item, active: true } : item
          )
        );
               // Show success message
        toast.success("Report Activated Successfully");
        setError({ message: "Report Activated Successfully", isSuccess: true });
      } catch (err) {
        console.error('Error activating report:', err);
        toast.error(`Failed to activate report: ${err.message}`);
        setError(`Failed to activate report: ${err.message}`);
      } finally {
        setLoading(false);
      }
    }
  };
    // Function to find if the selected report is active
  const isSelectedReportActive = () => {
    if (!selectedReportId) return false;
    const report = filteredReports.find(r => r.id === selectedReportId);
    return report ? report.active : false;
  };
  
  // Render menu dropdown
  const renderMenuDropdown = () => {
    return (
      <select id="menuDropdown" onChange={handleMenuChange}>
        <option value="">Select</option>
        {menuItems.map((menu) => (
          <option key={menu.menuId} value={menu.menuId}>
            {menu.menuName}
          </option>
        ))}
      </select>
    );
  };
  
  return (
    <div className="manage-reports-page">
      {loading && <LoadingIndicator isLoading={true} />}
      
     
      
      <div className='selectMenu' >
        <label htmlFor="menu-select" >Select Menu</label>
        <div className="dropdown-wrapper">
          {renderMenuDropdown()}
         
        </div>
      </div>
                  {showReportList && (
        <div className="report-management-panel">
          {/* Buttons for report management */}
          <div className="button-toolbar">
            <button 
              className="gray-button" 
              onClick={handleAddReport}
              title="Add New Report"
            >
              Add
            </button>
            <button 
              className="gray-button" 
              onClick={handleEditReport}
              title="Edit Selected Report"
              disabled={!selectedReportId}
            >
              Edit
            </button>
                        {/* Show Inactive button only when an active report is selected */}
            {selectedReportId && isSelectedReportActive() && (
              <button 
                className="gray-button" 
                onClick={handleDeactivateReport}
                title="Inactivate Selected Report"
              >
                Inactivate
              </button>
            )}
                        {/* Show Active button only when an inactive report is selected */}
            {selectedReportId && !isSelectedReportActive() && (
              <button 
                className="gray-button" 
                onClick={handleActivateReport}
                title="Activate Selected Report"
              >
                Activate
              </button>
            )}
          </div>
                    {error && (
            <div className={`status-message ${error.isSuccess ? 'success' : 'error'}`}>
              <span>{error.message || error}</span>
              <button
                type="button"
                className="close-btn"
                aria-label="Close notification"
                onClick={() => setError(null)}
              >
                &times;
              </button>
            </div>
          )}
          
          <div className="grid-container">
            <table className="reports-table">
              <thead>
                <tr>
                  <th width="30px"></th>
                  <th className="report-name-col">Report Name</th>
                  <th className="report-desc-col">Report Description</th>
                  {showKeywordTags && <th className="report-keywords-col">Keywords & Tags</th>}
                    <th className="active-col">Active</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.length > 0 ? (
                  filteredReports.map((report, idx) => (
                    <tr 
                      key={report.id}
                      className={
                        `${idx % 2 === 0 ? 'even-row' : 'odd-row'}${report.active ? '' : ' inactive-report'}`
                      }
                    >
                      <td>
                        <input 
                          type="radio" 
                          name="reportSelection" 
                          checked={selectedReportId === report.id}
                          onChange={() => handleReportSelection(report.id)}
                        />
                      </td>
                      <td className="report-name-cell">{report.name}</td>
                      <td className="report-desc-cell">{report.description}</td>
                      {showKeywordTags && <td className="report-keywords-cell">{report.keywords}</td>}
                        <td className="active-col">{report.active ? 'True' : 'False'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={showKeywordTags ? 5 : 4} className="no-reports-message">
                      No reports found for this menu
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageReportsPage;